import React, { useState, useEffect, useRef } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import {
  Card,
  Form,
  Button,
  Tabs,
  Tab,
  Modal,
  Tooltip,
  OverlayTrigger,
} from "react-bootstrap";
import axios from "axios";
import { API_URL_LOCAL, formatDateAccordingTo } from "..";
import { Icon } from "@iconify/react/dist/iconify.js";
import $ from "jquery";
import "datatables.net-dt/js/dataTables.dataTables.js";
import "datatables.net-responsive-dt/js/responsive.dataTables.js";
import "datatables.net-responsive-dt/css/responsive.dataTables.css";
import { Link, useNavigate } from "react-router-dom";

const formatTimeInto = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "00:00:00";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0"
  )}:${String(remainingSeconds).padStart(2, "0")}`;
};

const TimesheetPage = () => {
  const history = useNavigate()
  const [activeTab, setActiveTab] = useState(
    localStorage.getItem("currentScreenTab") || "daily"
  );
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dailyFilter, setDailyFilter] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [weeklyFilter, setWeeklyFilter] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = day === 0 ? 6 : day - 1;
    return new Date(today.setDate(today.getDate() - diff))
      .toISOString()
      .split("T")[0];
  });
  const [monthlyFilter, setMonthlyFilter] = useState(
    new Date().toISOString().slice(0, 7)
  );
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [tooltipData, setTooltipData] = useState({});

  const dailyTableRef = useRef(null);
  const weeklyTableRef = useRef(null);
  const monthlyTableRef = useRef(null);
  const tableInstanceRef = useRef({ daily: null, weekly: null, monthly: null });

  const API_ENDPOINTS = {
    daily: `${API_URL_LOCAL}/api/attendance/timesheet/daily`,
    weekly: `${API_URL_LOCAL}/api/attendance/timesheet/weekly`,
    monthly: `${API_URL_LOCAL}/api/attendance/timesheet/monthly`,
  };

  // Fetch data based on active tab
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user || !user.id) {
          throw new Error("User not authenticated. Please log in.");
        }
        let response;
        if (activeTab === "daily") {
          const obj = {
            date: dailyFilter,
            org_id: user.id,
          };
          response = await axios.post(API_ENDPOINTS.daily, obj, {
            headers: { Authorization: `Bearer ${user.token}` },
          });
        } else if (activeTab === "weekly") {
          const obj = {
            startDate: weeklyFilter,
            org_id: user.id,
          };
          response = await axios.post(API_ENDPOINTS.weekly, obj, {
            headers: { Authorization: `Bearer ${user.token}` },
          });
        } else if (activeTab === "monthly") {
          const obj = {
            month: monthlyFilter,
            org_id: user.id,
          };
          response = await axios.post(API_ENDPOINTS.monthly, obj, {
            headers: { Authorization: `Bearer ${user.token}` },
          });
        }

        if (!response.data || !Array.isArray(response.data.data)) {
          throw new Error("Invalid data format received from the server.");
        }
        setTimesheets(response.data.data);
      } catch (err) {
        let errorMessage = "An error occurred while fetching timesheets.";
        if (err.response) {
          errorMessage =
            err.response.data.message || `Server error: ${err.response.status}`;
        } else if (err.request) {
          errorMessage = "Network error: Unable to reach the server.";
        } else {
          errorMessage = err.message;
        }
        setError(errorMessage);
        setTimesheets([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeTab, dailyFilter, weeklyFilter, monthlyFilter]);

  // Cleanup DataTable instance
  const cleanupTable = (tableRef, tab) => {
    if (tableInstanceRef.current[tab] && tableRef.current) {
      try {
        $(tableRef.current).off("click"); // Remove event listeners
        tableInstanceRef.current[tab].destroy(false); // Destroy without removing DOM
        tableInstanceRef.current[tab] = null;
      } catch (err) {
        console.warn(`Error during DataTable cleanup for ${tab}:`, err);
      }
    }
  };

  // Common DataTable options
  const dataTableOptions = {
    pageLength: 10,
    lengthMenu: [10, 20, 50],
    order: [[0, "asc"]],
    language: {
      emptyTable: `No timesheet data for this ${activeTab} period`,
    },
    destroy: true,
    // responsive: true,
  };

  // Daily DataTable
  const formatIsoToDateTime = (isoDateString) => {
    try {
      if (!isoDateString || typeof isoDateString !== "string") {
        return "-";
      }
      const date = new Date(isoDateString);
      if (isNaN(date.getTime())) {
        return "-";
      }
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const day = String(date.getUTCDate()).padStart(2, "0");
      const hours = String(date.getUTCHours()).padStart(2, "0");
      const minutes = String(date.getUTCMinutes()).padStart(2, "0");
      const seconds = String(date.getUTCSeconds()).padStart(2, "0");
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.error("Error formatting date:", error.message);
      return "-";
    }
  };

  useEffect(() => {
    if (!loading && dailyTableRef.current && activeTab === "daily") {
      const columns = [
        { data: "employee_name", title: "Employee Name" },
        {
          data: "check_in",
          title: "Check In Time",
          render: (data) => {
            if (data) {
              const value = formatIsoToDateTime(data);
              return formatDateAccordingTo(value);
            }
            return "-";
          },
        },
        {
          data: "check_out",
          title: "Check Out Time",
          render: (data) => {
            if (data) {
              const value = formatIsoToDateTime(data);
              return formatDateAccordingTo(value);
            }
            return "-";
          },
        },
        {
          data: "hours",
          title: "Total Working Hours",
          render: (data) => formatTimeInto(parseInt(data, 10) || 0),
        },
        {
          data: "status",
          title: "Status",
          render: (data) => {
            const className =
              data === "Present"
                ? "bg-success-50 text-success-600 border-success-50"
                : data === "Absent"
                ? "bg-danger-50 text-danger-600 border-danger-50"
                : "bg-warning-50 text-warning-600 border-warning-50";
            return `<span class="badge ${className}">${data}</span>`;
          },
        },
        {
          data: null,
          title: "Action",
          orderable: false,
          render: (data, type, row) => {
            let buttons = `
              <div class="d-flex gap-2">
                <button class="btn btn-outline-primary px-8 py-4 radius-8 view-btn" data-id="${row.id}">
                  <i class="ph ph-eye" style="font-size: 18px;"></i>
                </button>
            `;
            if (row.status === "Pending") {
              buttons += `
                <button class="btn btn-outline-success px-8 py-4 radius-8 approve-btn" data-id="${row.id}">
                  <i class="ph ph-check" style="font-size: 18px;"></i>
                </button>
                <button class="btn btn-outline-danger px-8 py-4 radius-8 reject-btn" data-id="${row.id}">
                  <i class="ph ph-x" style="font-size: 18px;"></i>
                </button>
              `;
            }
            buttons += `</div>`;
            return buttons;
          },
        },
      ];

      cleanupTable(dailyTableRef, "daily");

      tableInstanceRef.current.daily = $(dailyTableRef.current).DataTable({
        ...dataTableOptions,
        data: timesheets,
        columns,
      });

      $(dailyTableRef.current).on("click", ".view-btn", function () {
        const id = $(this).data("id");
        const timesheet = timesheets.find((t) => t.id === id);
        setModalData(timesheet);
        setShowModal(true);
      });

      $(dailyTableRef.current).on("click", ".approve-btn", function () {
        const id = $(this).data("id");
        handleStatusChange(id, "Approved");
      });

      $(dailyTableRef.current).on("click", ".reject-btn", function () {
        const id = $(this).data("id");
        handleStatusChange(id, "Rejected");
      });

      return () => {
        cleanupTable(dailyTableRef, "daily");
      };
    }
  }, [timesheets, loading, activeTab]);

  // Weekly/Monthly DataTable
  const initializeGroupedTable = (tableRef, filter, days, tab) => {
    if (!loading && tableRef.current && (activeTab === "weekly" || activeTab === "monthly")) {
      const startDate =
        activeTab === "weekly" ? new Date(filter) : new Date(`${filter}-01`);
      const dates = Array.from({ length: days }, (_, i) => {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + i);
        return d;
      });
  
      // Group data by employee and collect tooltip data
      const groupedData = {};
      const newTooltipData = {};
      timesheets.forEach((t) => {
        if (!groupedData[t.employee_name]) {
          groupedData[t.employee_name] = {
            employee_name: t.employee_name,
            hours: Array(days).fill(0),
            details: Array(days).fill(null),
            total_hours: 0,
          };
        }
        const dayIndex = dates.findIndex(
          (d) => d.toISOString().split("T")[0] === t.date
        );
        if (dayIndex !== -1) {
          const seconds = parseInt(t.hours, 10) || 0;
          groupedData[t.employee_name].hours[dayIndex] = seconds;
          groupedData[t.employee_name].details[dayIndex] = t;
          groupedData[t.employee_name].total_hours += seconds;
          newTooltipData[`${t.employee_name}-${t.date}`] = {
            check_in: t.check_in === null ? '-' : formatDateAccordingTo(formatIsoToDateTime(t.check_in)),
            check_out: t.check_out === null ? '-' : formatDateAccordingTo(formatIsoToDateTime(t.check_out)),
            shift_start: t.shift_start || '-',
            shift_end: t.shift_end || '-',
            hours: formatTimeInto(seconds),
            status: t.check_in === null ? 'Absent' : 'Present',
            emp_id: t.id,
            date: t.date,
          };
        }
      });
      const data = Object.values(groupedData);
      setTooltipData(newTooltipData);
  
      const columns = [
        { data: "employee_name", title: "Employee Name"},
        ...dates.map((date, i) => ({
          data: `hours[${i}]`,
          title: date.toLocaleDateString([], {
            weekday: "short",
            day: "numeric",
            month: "short",
          }),
          render: (data, type, row) => {
            if (data === null || data === 0) return "No Data Found";
            const formatted = date.toISOString().split("T")[0];
            const now = new Date();
            const currentDate = `${now.getFullYear()}-${String(
              now.getMonth() + 1
            ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
            if (formatted > currentDate) {
              return `<span>schedule off</span>`;
            }
            const detail = row.details[i];
            if (!detail) return "absent";
            if (formatted === detail?.date) {
              // Add on-press class and data-id for redirection
              return `<span class="tooltip-cell on-press" data-id="${detail.id}" data-tooltip-key="${row.employee_name}-${detail.date}">${formatTimeInto(detail.hours)}</span>`;
            }
            return "";
          },
        })),
        {
          data: "total_hours",
          title: "Total Hours",
          render: (data) => formatTimeInto(data),
        },
      ];
  
      cleanupTable(tableRef, tab);
  
      tableInstanceRef.current[tab] = $(tableRef.current).DataTable({
        ...dataTableOptions,
        data,
        columns,
      });
  
      // Add click event handler for redirection
      if (tab === "weekly") {
        $(tableRef.current).on("click", ".on-press", function () {
          const id = $(this).data("id");
          history(`/time-sheet/routine/${id}`); // Navigate to the routine page
        });
      }
  
      return () => {
        cleanupTable(tableRef, tab);
        // Remove event listener to prevent memory leaks
        if (tab === "weekly") {
          $(tableRef.current).off("click", ".on-press");
        }
      };
    }
  };
  // Weekly DataTable
  useEffect(() => {
    if (activeTab === "weekly") {
      return initializeGroupedTable(weeklyTableRef, weeklyFilter, 7, "weekly");
    }
  }, [timesheets, loading, activeTab, weeklyFilter]);

  // Monthly DataTable
  useEffect(() => {
    if (activeTab === "monthly") {
      const daysInMonth = new Date(
        monthlyFilter.split("-")[0],
        monthlyFilter.split("-")[1],
        0
      ).getDate();
      return initializeGroupedTable(monthlyTableRef, monthlyFilter, daysInMonth, "monthly");
    }
  }, [timesheets, loading, activeTab, monthlyFilter]);

  // Apply React-Bootstrap tooltips
  useEffect(() => {
    if (activeTab === "weekly" || activeTab === "monthly") {
      const table =
        activeTab === "weekly"
          ? weeklyTableRef.current
          : monthlyTableRef.current;
      if (table) {
        const tooltipCells = table.querySelectorAll(".tooltip-cell");
        tooltipCells.forEach((cell) => {
          const tooltipKey = cell.getAttribute("data-tooltip-key");
          const data = tooltipData[tooltipKey];
          if (data && cell.parentNode) {
            const wrapper = document.createElement("div");
            wrapper.className = "tooltip-wrapper";
            cell.parentNode.replaceChild(wrapper, cell);
            console.log(data);
            import("react-dom").then((ReactDOM) => {
              ReactDOM.render(
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      <div
                        style={{ paddingLeft: 5, paddingRight: 5, width: 200 }}
                      >
                        
                        <div style={{ display: "flex", flexDirection: "row" }}>
                          <div
                            style={{
                              paddingBottom: 5,
                              paddingTop: 5,
                              width: "50%",
                              textAlign: "left",
                              fontSize: 14,
                            }}
                          >
                            Status:
                          </div>
                          <div
                            style={{
                              paddingBottom: 5,
                              paddingTop: 5,
                              width: "50%",
                              fontSize: 14,
                            }}
                          >
                            {data.status}
                          </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "row" }}>
                          <div
                            style={{
                              paddingBottom: 5,
                              paddingTop: 5,
                              width: "50%",
                              textAlign: "left",
                              fontSize: 14,
                            }}
                          >
                            Check In:
                          </div>
                          <div
                            style={{
                              paddingBottom: 5,
                              paddingTop: 5,
                              width: "50%",
                              fontSize: 14,
                            }}
                          >
                            {data.check_in}
                          </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "row" }}>
                          <div
                            style={{
                              paddingBottom: 5,
                              paddingTop: 5,
                              width: "50%",
                              textAlign: "left",
                              fontSize: 14,
                            }}
                          >
                            Check Out:
                          </div>
                          <div
                            style={{
                              paddingBottom: 5,
                              paddingTop: 5,
                              width: "50%",
                              fontSize: 14,
                            }}
                          >
                            {data.check_out}
                          </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "row" }}>
                          <div
                            style={{
                              paddingBottom: 5,
                              paddingTop: 5,
                              width: "50%",
                              textAlign: "left",
                              fontSize: 14,
                            }}
                          >
                            Shift Start:
                          </div>
                          <div
                            style={{
                              paddingBottom: 5,
                              paddingTop: 5,
                              width: "50%",
                              fontSize: 14,
                            }}
                          >
                            {data.shift_start}
                          </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "row" }}>
                          <div
                            style={{
                              paddingBottom: 5,
                              paddingTop: 5,
                              width: "50%",
                              textAlign: "left",
                              fontSize: 14,
                            }}
                          >
                            Shift End:
                          </div>
                          <div
                            style={{
                              paddingBottom: 5,
                              paddingTop: 5,
                              width: "50%",
                              fontSize: 14,
                            }}
                          >
                            {data.shift_end}
                          </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "row" }}>
                          <div
                            style={{
                              paddingBottom: 5,
                              paddingTop: 5,
                              width: "50%",
                              textAlign: "left",
                              fontSize: 14,
                            }}
                          >
                            Total Hours:
                          </div>
                          <div
                            style={{
                              paddingBottom: 5,
                              paddingTop: 5,
                              width: "50%",
                              fontSize: 14,
                            }}
                          >
                            {data.hours}
                          </div>
                        </div>
                      </div>
                    </Tooltip>
                  }
                >
                  <span>{cell.textContent}</span>
                </OverlayTrigger>,
                wrapper
              );
            });
          }
        });
      }
    }
  }, [tooltipData, activeTab]);

  // Handle status change
  const handleStatusChange = async (id, status) => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user || !user.token) {
        throw new Error("User not authenticated.");
      }

      const response = await axios.put(
        `${API_URL_LOCAL}/api/timesheet/${user.id}`,
        { status },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      if (response.status === 200) {
        setTimesheets(
          timesheets.map((t) => (t.id === id ? { ...t, status } : t))
        );
        alert(`Timesheet ${status.toLowerCase()} successfully!`);
      } else {
        throw new Error(`Failed to ${status.toLowerCase()} timesheet`);
      }
    } catch (err) {
      alert(`Failed to ${status.toLowerCase()} timesheet: ${err.message}`);
    }
  };

  // Handle filter changes
  const handleDailyFilter = (e) => {
    setDailyFilter(e.target.value);
  };

  const handleWeeklyFilter = (e) => {
    const date = new Date(e.target.value);
    const day = date.getDay();
    const diff = day === 0 ? 6 : day - 1;
    const monday = new Date(date.setDate(date.getDate() - diff))
      .toISOString()
      .split("T")[0];
    setWeeklyFilter(monday);
  };

  const handleMonthlyFilter = (e) => {
    setMonthlyFilter(e.target.value);
  };

  // Handle tab change with page reload
  const handleTabChange = (tab) => {
    localStorage.setItem("currentScreenTab", tab);
    window.location.reload();
  };

  if (loading) {
    return <div className="text-center">Loading...</div>;
  }

  if (error) {
    return (
      <div className="alert alert-danger bg-danger-50 text-danger-600 border-danger-50 px-24 py-11 mb-20 fw-semibold text-lg radius-8">
        {error}
      </div>
    );
  }

  return (
    <MasterLayout>
      <Breadcrumb title="Timesheet" />
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          <Tabs
            activeKey={activeTab}
            onSelect={handleTabChange}
            className="mb-20"
          >
            <Tab eventKey="daily" title="Daily" />
            <Tab eventKey="weekly" title="Weekly" />
            <Tab eventKey="monthly" title="Monthly" />
          </Tabs>
          <div className="d-flex justify-content-between mb-20">
            <h6 className="fw-semibold text-primary-dark">
              {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}{" "}
              Timesheets
            </h6>
            <Form.Group>
              {activeTab === "daily" && (
                <Form.Control
                  type="date"
                  value={dailyFilter}
                  onChange={handleDailyFilter}
                  className="radius-8"
                />
              )}
              {activeTab === "weekly" && (
                <Form.Control
                  type="date"
                  value={weeklyFilter}
                  onChange={handleWeeklyFilter}
                  className="radius-8"
                />
              )}
              {activeTab === "monthly" && (
                <Form.Control
                  type="month"
                  value={monthlyFilter}
                  onChange={handleMonthlyFilter}
                  className="radius-8"
                />
              )}
            </Form.Group>
          </div>
          <div className="table-responsive">
            {activeTab === "daily" && (
              <table
                id="dataTable"
                ref={dailyTableRef}
                className="table table-striped table-bordered"
                style={{ width: "100%" }}
              >
                <thead>
                  <tr>
                    <th>Employee Name</th>
                    <th>Check In Time</th>
                    <th>Check Out Time</th>
                    <th>Total Working Hours</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody></tbody>
              </table>
            )}
            {activeTab === "weekly" && (
              <table
                ref={weeklyTableRef}
                className="table table-striped table-bordered"
                style={{ width: "100%" }}
              >
                <thead>
                  <tr>
                    <th>Employee Name</th>
                    {Array.from({ length: 7 }, (_, i) => {
                      const d = new Date(weeklyFilter);
                      d.setDate(d.getDate() + i);
                      return (
                        <th key={i}>
                          {d.toLocaleDateString([], {
                            weekday: "short",
                            day: "numeric",
                            month: "short",
                          })}
                        </th>
                      );
                    })}
                    <th>Total Hours</th>
                  </tr>
                </thead>
                <tbody></tbody>
              </table>
            )}
            {activeTab === "monthly" && (
              <table
                ref={monthlyTableRef}
                className="table table-striped table-bordered"
                style={{ width: "100%" }}
              >
                <thead>
                  <tr>
                    <th>Employee Name</th>
                    {Array.from(
                      {
                        length: new Date(
                          monthlyFilter.split("-")[0],
                          monthlyFilter.split("-")[1],
                          0
                        ).getDate(),
                      },
                      (_, i) => {
                        const d = new Date(`${monthlyFilter}-${i + 1}`);
                        return (
                          <th key={i}>
                            {d.toLocaleDateString([], {
                              day: "numeric",
                              month: "short",
                            })}
                          </th>
                        );
                      }
                    )}
                    <th>Total Hours</th>
                  </tr>
                </thead>
                <tbody></tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Timesheet Modal */}
      <Modal
        style={{ zIndex: 9999 }}
        show={showModal}
        onHide={() => setShowModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <h6>
              <Icon fontSize={22} icon="mdi:clock" className="me-2" />
              Timesheet Details
            </h6>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalData && (
            <div>
              <p>
                <strong>Employee:</strong> {modalData.employee_name}
              </p>
              <p>
                <strong>Date:</strong>{" "}
                {new Date(modalData.date).toLocaleDateString()}
              </p>
              <p>
                <strong>Check In:</strong>{" "}
                {modalData.check_in == null
                  ? "-"
                  : formatDateAccordingTo(formatIsoToDateTime(modalData.check_in))
                }
              </p>
              <p>
                <strong>Check Out:</strong>{" "}
                {modalData.check_out !== null
                  ? formatDateAccordingTo(formatIsoToDateTime(modalData.check_out))
                  : "-"}
              </p>
              <p>
                <strong>Shift Start:</strong> {modalData.shift_start || "-"}
              </p>
              <p>
                <strong>Shift End:</strong> {modalData.shift_end || "-"}
              </p>
              <p>
                <strong>Total Hours:</strong>{" "}
                {formatTimeInto(parseInt(modalData.hours, 10) || 0)}
              </p>
              <p>
                <strong>Status:</strong> {modalData.status}
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            className="text-md px-24 py-8 radius-8"
            onClick={() => setShowModal(false)}
          >
            <Icon fontSize={22} icon="mdi:close" className="me-2" />
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </MasterLayout>
  );
};

export default TimesheetPage;